import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Download, Mail, LogOut, Edit3, Trash2, Plus, X, Printer } from 'lucide-react';
import { api, API_BASE, SPORTS } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import IdCard from '@/components/IdCard';

const STATUSES = ['all', 'pending', 'approved', 'selected', 'rejected'];
const MATCH_STATUS = ['upcoming', 'live', 'completed', 'cancelled'];

export default function AdminDashboard() {
  const [section, setSection] = useState('registrations');
  const [status, setStatus] = useState('all');
  const [stats, setStats] = useState({});
  const [regs, setRegs] = useState([]);
  const [matches, setMatches] = useState([]);
  const [players, setPlayers] = useState([]);
  const [playerStats, setPlayerStats] = useState([]);
  const [picked, setPicked] = useState(null);
  const [cardReg, setCardReg] = useState(null);
  const [matchForm, setMatchForm] = useState(null);
  const [emailTo, setEmailTo] = useState('');
  const navigate = useNavigate();

  const logout = () => { localStorage.removeItem('psf_token'); navigate('/admin/login'); };

  const loadStats = useCallback(async () => {
    try { setStats((await api.get('/admin/stats')).data); } catch {}
  }, []);
  const loadRegs = useCallback(async () => {
    try { setRegs((await api.get(`/admin/registrations?status=${status}`)).data); } catch {}
  }, [status]);
  const loadMatches = useCallback(async () => {
    try {
      const [m, p, ps] = await Promise.all([
        api.get('/admin/matches'), api.get('/admin/players'), api.get('/admin/player-stats'),
      ]);
      setMatches(m.data); setPlayers(p.data); setPlayerStats(ps.data);
    } catch {}
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { if (section === 'registrations') loadRegs(); else loadMatches(); }, [section, loadRegs, loadMatches]);

  const review = async (id, action, remarks = '') => {
    try {
      await api.post(`/admin/registrations/${id}/review`, { action, remarks });
      toast.success(action === 'approve' ? 'Approved. ID card generated.' : 'Rejected.');
      setPicked(null); loadRegs(); loadStats();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const downloadExcel = async () => {
    try {
      const r = await fetch(`${API_BASE}/admin/export/excel?status=${status}`, { headers: { Authorization: `Bearer ${localStorage.getItem('psf_token')}` } });
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `PGSF-Registrations.xlsx`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Could not download'); }
  };

  const sendEmail = async () => {
    if (!/^\S+@\S+\.\S+$/.test(emailTo)) { toast.error('Enter a valid email'); return; }
    try {
      const { data } = await api.post('/admin/export/email', { recipient_email: emailTo });
      if (data.sent) toast.success(`Excel emailed to ${emailTo}`); else toast.error('Email send failed');
    } catch { toast.error('Email send failed'); }
  };

  const saveMatch = async () => {
    const m = matchForm;
    try {
      if (m._id) {
        const payload = { ...m }; delete payload._id;
        await api.put(`/admin/matches/${m._id}`, payload);
      } else {
        await api.post('/admin/matches', m);
      }
      toast.success('Saved'); setMatchForm(null); loadMatches(); loadStats();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const deleteMatch = async (id) => {
    if (!window.confirm('Delete this match?')) return;
    try { await api.delete(`/admin/matches/${id}`); loadMatches(); loadStats(); } catch { toast.error('Failed'); }
  };

  return (
    <main className="min-h-screen bg-[#0A0A0A] text-white pt-24 pb-16 grain" data-testid="admin-dash">
      <div className="mx-auto max-w-[1600px] px-5 md:px-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#CCFF00]">Admin Dashboard</p>
            <h1 className="font-display uppercase text-3xl md:text-5xl leading-tight tracking-tight">Control Room</h1>
          </div>
          <button onClick={logout} className="flex items-center gap-2 border border-white/20 px-4 py-2 font-mono text-xs uppercase tracking-widest hover:bg-white hover:text-black" data-testid="btn-logout"><LogOut className="h-3 w-3" />Logout</button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-8">
          {[
            ['Total', stats.total, '#CCFF00'], ['Pending', stats.pending, '#CCFF00'],
            ['Approved', stats.approved, '#007AFF'], ['Selected', stats.selected, '#30D158'],
            ['Rejected', stats.rejected, '#FF3B30'],
            ['Matches', stats.matches_total, '#8892B0'], ['Upcoming', stats.matches_upcoming, '#CCFF00'],
            ['Completed', stats.matches_completed, '#007AFF'],
          ].map(([k, v, c]) => (
            <div key={k} className="border border-white/10 bg-[#141414] p-4" data-testid={`stat-${k.toLowerCase()}`}>
              <p className="font-mono text-[9px] uppercase tracking-widest text-white/50">{k}</p>
              <p className="font-display text-3xl" style={{ color: c }}>{v ?? 0}</p>
            </div>
          ))}
        </div>

        {/* Section switcher */}
        <div className="flex items-center gap-2 border-b border-white/10 mb-5" data-testid="section-switch">
          {['registrations', 'matches'].map((s) => (
            <button key={s} onClick={() => setSection(s)} className={`px-4 py-2 font-mono text-xs uppercase tracking-widest ${section === s ? 'text-[#CCFF00] border-b-2 border-[#CCFF00]' : 'text-white/60 hover:text-white'}`} data-testid={`sec-${s}`}>
              {s === 'registrations' ? 'Registrations' : 'Match Data'}
            </button>
          ))}
        </div>

        {section === 'registrations' && (
          <>
            <div className="flex flex-wrap items-center gap-3 mb-5" data-testid="status-tabs">
              {STATUSES.map((s) => (
                <button key={s} onClick={() => setStatus(s)} className={`font-mono text-[10px] uppercase tracking-widest px-3 py-2 border ${status === s ? 'bg-white text-black border-white' : 'border-white/20 text-white/70 hover:border-white'}`} data-testid={`tab-${s}`}>{s}</button>
              ))}
              <div className="flex-1" />
              <button onClick={downloadExcel} className="inline-flex items-center gap-2 bg-[#CCFF00] text-black px-4 py-2 font-mono text-[10px] uppercase tracking-widest hover:bg-white" data-testid="btn-xlsx"><Download className="h-3 w-3" />Download Excel</button>
              <div className="flex items-stretch">
                <Input placeholder="federation@example.com" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} className="bg-black border-white/20 rounded-none h-9 w-56" data-testid="input-email-to" />
                <button onClick={sendEmail} className="inline-flex items-center gap-2 bg-[#007AFF] text-white px-4 font-mono text-[10px] uppercase tracking-widest hover:bg-[#CCFF00] hover:text-black" data-testid="btn-email-xlsx"><Mail className="h-3 w-3" />Email</button>
              </div>
            </div>

            <div className="overflow-x-auto border border-white/10">
              <table className="w-full text-sm" data-testid="regs-table">
                <thead className="bg-[#141414] text-white/60 font-mono text-[10px] uppercase tracking-widest">
                  <tr>
                    <th className="text-left p-3">App No.</th><th className="text-left p-3">Name</th>
                    <th className="text-left p-3">Sport</th><th className="text-left p-3">Phone</th>
                    <th className="text-left p-3">Status</th><th className="text-left p-3">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {regs.map((r) => (
                    <tr key={r._id} className="border-t border-white/10 hover:bg-white/5" data-testid={`reg-${r._id}`}>
                      <td className="p-3 font-mono">{r.application_no}</td>
                      <td className="p-3">{r.full_name}</td>
                      <td className="p-3">{r.sport}</td>
                      <td className="p-3 font-mono">{r.phone}</td>
                      <td className="p-3"><span className="font-mono text-[10px] uppercase px-2 py-1 border" style={{ color: r.status === 'approved' ? '#007AFF' : r.status === 'selected' ? '#30D158' : r.status === 'rejected' ? '#FF3B30' : '#CCFF00' }}>{r.status}</span></td>
                      <td className="p-3 flex gap-2">
                        <button onClick={() => setPicked(r)} className="border border-white/20 px-3 py-1 font-mono text-[10px] uppercase hover:bg-white hover:text-black" data-testid={`open-${r._id}`}>Open</button>
                        {(r.status === 'approved' || r.status === 'selected') && <button onClick={() => setCardReg(r)} className="border border-[#CCFF00]/60 text-[#CCFF00] px-3 py-1 font-mono text-[10px] uppercase hover:bg-[#CCFF00] hover:text-black" data-testid={`card-${r._id}`}>ID Card</button>}
                      </td>
                    </tr>
                  ))}
                  {regs.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-white/50 font-mono text-xs">No records.</td></tr>}
                </tbody>
              </table>
            </div>
          </>
        )}

        {section === 'matches' && (
          <>
            <div className="flex items-center gap-3 mb-4">
              <button onClick={() => setMatchForm({ title: '', sport: 'Cricket', match_date: '', venue: '', team_a: '', team_b: '', score_a: 0, score_b: 0, winner: '', status: 'upcoming', player_ids: [], notes: '' })} className="inline-flex items-center gap-2 bg-[#CCFF00] text-black px-4 py-2 font-mono text-[10px] uppercase tracking-widest hover:bg-white" data-testid="btn-new-match"><Plus className="h-3 w-3" />New Match</button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {matches.map((m) => (
                <div key={m._id} className="border border-white/10 bg-[#141414] p-4" data-testid={`match-${m._id}`}>
                  <div className="flex items-center justify-between mb-2 font-mono text-[10px] uppercase tracking-widest">
                    <span className="text-white/60">{m.sport}</span>
                    <span style={{ color: m.status === 'live' ? '#FF3B30' : m.status === 'completed' ? '#007AFF' : '#CCFF00' }}>{m.status}</span>
                  </div>
                  <div className="font-heading text-base font-bold">{m.title || `${m.team_a} vs ${m.team_b}`}</div>
                  <div className="text-xs text-white/60 mt-1">{m.venue} · {m.match_date}</div>
                  <div className="font-display text-3xl mt-3">{m.score_a} <span className="text-white/40 text-xl">—</span> {m.score_b}</div>
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => setMatchForm({ ...m })} className="flex-1 border border-white/20 py-2 font-mono text-[10px] uppercase hover:bg-white hover:text-black inline-flex items-center justify-center gap-1" data-testid={`edit-${m._id}`}><Edit3 className="h-3 w-3" />Edit</button>
                    <button onClick={() => deleteMatch(m._id)} className="border border-[#FF3B30]/40 text-[#FF3B30] px-3 py-2 font-mono text-[10px] uppercase hover:bg-[#FF3B30] hover:text-white inline-flex items-center" data-testid={`del-${m._id}`}><Trash2 className="h-3 w-3" /></button>
                  </div>
                </div>
              ))}
              {matches.length === 0 && <div className="col-span-full text-center text-white/50 font-mono text-xs py-10">No matches yet.</div>}
            </div>

            {playerStats.length > 0 && (
              <div className="mt-10">
                <h2 className="font-display text-2xl mb-3">Player Participation</h2>
                <div className="overflow-x-auto border border-white/10">
                  <table className="w-full text-sm" data-testid="player-stats">
                    <thead className="bg-[#141414] text-white/60 font-mono text-[10px] uppercase tracking-widest">
                      <tr><th className="text-left p-3">Player ID</th><th className="text-left p-3">Name</th><th className="text-left p-3">Sport</th><th className="text-left p-3">Played</th><th className="text-left p-3">Upcoming</th></tr>
                    </thead>
                    <tbody>
                      {playerStats.map((p) => (
                        <tr key={p.player_id} className="border-t border-white/10">
                          <td className="p-3 font-mono">{p.player_id}</td><td className="p-3">{p.full_name}</td>
                          <td className="p-3">{p.sport}</td><td className="p-3">{p.played}</td><td className="p-3">{p.upcoming}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Registration detail modal */}
      {picked && <RegModal reg={picked} onClose={() => setPicked(null)} onReview={review} />}

      {/* ID card modal */}
      {cardReg && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur overflow-y-auto p-4" onClick={() => setCardReg(null)} data-testid="card-modal">
          <div className="min-h-full flex items-center justify-center">
            <div className="w-full max-w-[800px] space-y-4" onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-between items-center">
                <h3 className="font-display text-xl">ID Card · {cardReg.full_name}</h3>
                <div className="flex gap-2">
                  <button onClick={() => window.print()} className="inline-flex items-center gap-2 bg-[#CCFF00] text-black px-4 py-2 font-mono text-[10px] uppercase tracking-widest hover:bg-white" data-testid="print-btn"><Printer className="h-3 w-3" />Print</button>
                  <button onClick={() => setCardReg(null)} className="border border-white/20 px-4 py-2 font-mono text-[10px] uppercase hover:bg-white hover:text-black">Close</button>
                </div>
              </div>
              <IdCard data={cardReg} />
            </div>
          </div>
        </div>
      )}

      {/* Match form modal */}
      {matchForm && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur overflow-y-auto p-4" onClick={() => setMatchForm(null)} data-testid="match-modal">
          <div className="min-h-full flex items-center justify-center">
            <div className="w-full max-w-xl border border-[#CCFF00]/40 bg-[#141414] p-6 space-y-3" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between">
                <h3 className="font-display text-xl">{matchForm._id ? 'Edit Match' : 'New Match'}</h3>
                <button onClick={() => setMatchForm(null)}><X className="h-4 w-4" /></button>
              </div>
              <F label="Title"><Input value={matchForm.title} onChange={(e) => setMatchForm({ ...matchForm, title: e.target.value })} className="bg-black border-white/20 rounded-none" data-testid="m-title" /></F>
              <div className="grid grid-cols-2 gap-3">
                <F label="Sport">
                  <Select value={matchForm.sport} onValueChange={(v) => setMatchForm({ ...matchForm, sport: v })}>
                    <SelectTrigger className="bg-black border-white/20 rounded-none"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-black border-white/20 max-h-60">{SPORTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </F>
                <F label="Date"><Input type="date" value={matchForm.match_date} onChange={(e) => setMatchForm({ ...matchForm, match_date: e.target.value })} className="bg-black border-white/20 rounded-none" data-testid="m-date" /></F>
              </div>
              <F label="Venue"><Input value={matchForm.venue} onChange={(e) => setMatchForm({ ...matchForm, venue: e.target.value })} className="bg-black border-white/20 rounded-none" data-testid="m-venue" /></F>
              <div className="grid grid-cols-2 gap-3">
                <F label="Team A"><Input value={matchForm.team_a} onChange={(e) => setMatchForm({ ...matchForm, team_a: e.target.value })} className="bg-black border-white/20 rounded-none" data-testid="m-team-a" /></F>
                <F label="Team B"><Input value={matchForm.team_b} onChange={(e) => setMatchForm({ ...matchForm, team_b: e.target.value })} className="bg-black border-white/20 rounded-none" data-testid="m-team-b" /></F>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <F label="Score A"><Input type="number" value={matchForm.score_a} onChange={(e) => setMatchForm({ ...matchForm, score_a: parseInt(e.target.value || '0', 10) })} className="bg-black border-white/20 rounded-none" /></F>
                <F label="Score B"><Input type="number" value={matchForm.score_b} onChange={(e) => setMatchForm({ ...matchForm, score_b: parseInt(e.target.value || '0', 10) })} className="bg-black border-white/20 rounded-none" /></F>
                <F label="Status">
                  <Select value={matchForm.status} onValueChange={(v) => setMatchForm({ ...matchForm, status: v })}>
                    <SelectTrigger className="bg-black border-white/20 rounded-none" data-testid="m-status"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-black border-white/20">{MATCH_STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </F>
              </div>
              <F label="Winner (optional)"><Input value={matchForm.winner} onChange={(e) => setMatchForm({ ...matchForm, winner: e.target.value })} className="bg-black border-white/20 rounded-none" /></F>
              <F label="Players">
                <div className="max-h-32 overflow-y-auto border border-white/15 p-2 space-y-1">
                  {players.map((p) => (
                    <label key={p.player_id} className="flex items-center gap-2 text-xs">
                      <input type="checkbox" checked={(matchForm.player_ids || []).includes(p.player_id)} onChange={(e) => {
                        const next = new Set(matchForm.player_ids || []);
                        if (e.target.checked) next.add(p.player_id); else next.delete(p.player_id);
                        setMatchForm({ ...matchForm, player_ids: [...next] });
                      }} /> <span>{p.full_name} <span className="text-white/40">· {p.sport} · {p.player_id}</span></span>
                    </label>
                  ))}
                  {players.length === 0 && <div className="text-white/40 text-xs font-mono">No approved players yet.</div>}
                </div>
              </F>
              <F label="Notes"><Input value={matchForm.notes} onChange={(e) => setMatchForm({ ...matchForm, notes: e.target.value })} className="bg-black border-white/20 rounded-none" /></F>
              <div className="flex justify-end gap-2 pt-2 border-t border-white/10">
                <button onClick={() => setMatchForm(null)} className="border border-white/20 px-4 py-2 font-mono text-[10px] uppercase hover:bg-white hover:text-black">Cancel</button>
                <button onClick={saveMatch} className="bg-[#CCFF00] text-black px-4 py-2 font-mono text-[10px] uppercase hover:bg-white" data-testid="m-save">Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

function F({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] font-mono uppercase tracking-widest text-white/60">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function RegModal({ reg, onClose, onReview }) {
  const [rejecting, setRejecting] = useState(false);
  const [remarks, setRemarks] = useState('');
  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur overflow-y-auto p-4" onClick={onClose} data-testid="reg-modal">
      <div className="min-h-full flex items-start justify-center py-6">
        <div className="w-full max-w-3xl border border-[#CCFF00]/40 bg-[#141414]" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-between p-4 border-b border-white/10">
            <div>
              <p className="font-mono text-[9px] uppercase tracking-widest text-white/50">{reg.application_no}</p>
              <h3 className="font-display text-2xl">{reg.full_name}</h3>
            </div>
            <button onClick={onClose}><X className="h-5 w-5" /></button>
          </div>
          <div className="p-4 grid grid-cols-1 md:grid-cols-[160px_1fr] gap-5">
            <div className="space-y-2">
              {reg.photo && <img src={reg.photo} alt="player" className="w-40 h-48 object-cover border border-white/20" data-testid="modal-photo" />}
              {reg.payment_screenshot && (
                <div>
                  <p className="font-mono text-[9px] uppercase tracking-widest text-[#CCFF00] mt-2">Payment Screenshot</p>
                  <img src={reg.payment_screenshot} alt="payment" className="w-40 h-40 object-cover border border-white/20" data-testid="modal-screenshot" />
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-2 text-sm">
              {[
                ['Sport', reg.sport], ['Category', reg.category], ['Event', reg.event], ['DOB', reg.dob],
                ['Gender', reg.gender], ['Phone', reg.phone], ['Email', reg.email], ['Aadhaar', reg.aadhaar],
                ['Guardian', reg.guardian_name], ['Guardian Phone', reg.guardian_phone],
                ['Institution', reg.institution], ['State', reg.state], ['Address', reg.address],
                ['UTR / Txn ID', reg.utr], ['Status', reg.status], ['Reg No.', reg.reg_no],
                ['Player ID', reg.player_id], ['Valid Till', reg.valid_till],
              ].map(([k, v]) => (
                <div key={k} className="border-b border-white/10 pb-2 min-w-0">
                  <div className="font-mono text-[9px] uppercase tracking-widest text-white/50">{k}</div>
                  <div className="text-white break-words">{v || '—'}</div>
                </div>
              ))}
            </div>
          </div>
          {reg.status !== 'rejected' && (
            <div className="p-4 border-t border-white/10 flex flex-wrap items-center gap-2">
              <button onClick={() => onReview(reg._id, 'approve')} className="bg-[#CCFF00] text-black px-4 py-2 font-mono text-[10px] uppercase tracking-widest hover:bg-white" data-testid="modal-approve">Approve</button>
              <button onClick={() => onReview(reg._id, 'select')} className="bg-[#30D158] text-black px-4 py-2 font-mono text-[10px] uppercase tracking-widest hover:bg-white" data-testid="modal-select">Select</button>
              <button onClick={() => onReview(reg._id, 'pending')} className="bg-[#8892B0] text-black px-4 py-2 font-mono text-[10px] uppercase tracking-widest hover:bg-white" data-testid="modal-pending">Mark Pending</button>
              {!rejecting ? (
                <button onClick={() => setRejecting(true)} className="bg-[#FF3B30] text-white px-4 py-2 font-mono text-[10px] uppercase tracking-widest hover:bg-white hover:text-black" data-testid="modal-reject">Reject</button>
              ) : (
                <div className="flex gap-2 flex-1 min-w-[280px]">
                  <Input value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Rejection reason" className="bg-black border-white/20 rounded-none" data-testid="modal-remarks" />
                  <button onClick={() => onReview(reg._id, 'reject', remarks)} className="bg-[#FF3B30] text-white px-4 py-2 font-mono text-[10px] uppercase hover:bg-white hover:text-black" data-testid="modal-confirm-reject">Confirm Reject</button>
                </div>
              )}
            </div>
          )}
          {reg.status === 'rejected' && (
            <div className="p-4 border-t border-white/10 flex flex-wrap items-center gap-2">
              <button onClick={() => onReview(reg._id, 'pending')} className="bg-[#8892B0] text-black px-4 py-2 font-mono text-[10px] uppercase tracking-widest hover:bg-white" data-testid="modal-reopen">Reopen (Pending)</button>
              <button onClick={() => onReview(reg._id, 'approve')} className="bg-[#CCFF00] text-black px-4 py-2 font-mono text-[10px] uppercase tracking-widest hover:bg-white" data-testid="modal-approve">Approve</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
